// ============================================================
// Strike-artifact export: render a structured memo to standalone
// legal-memo HTML, then download as Word (.doc) or print/save-as-PDF.
// ============================================================
import type { StrikeArtifact, MtdPair, MtdCount, MtdDefense, MtdSynth, MtdNotice, GapItem, AnswerResponse, AnswerDefense, AnswerCounterclaim, AmendDiffItem } from "../hooks/useStrikeArtifacts";

/** Strip raw [A#]/[E#] tag clusters left in prose — resolved arrays carry the citations. */
function stripTags(t: unknown): string {
  return String(t ?? "").replace(/\s*\[(?:\s*[AE]\d+\s*[,;]?\s*)+\]/g, "").replace(/\s{2,}/g, " ").trim();
}

function esc(s: unknown): string {
  return String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

export function memoHtml(a: StrikeArtifact): string {
  const c = a.content;
  const dt = new Date(c.generated_at ?? a.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  const sections = (c.prongs ?? []).map((p) => {
    let body = "";
    if (p.key === "exhibit_contradiction") {
      const rows = (p.items as MtdPair[]).map((i) => `
        <tr><td><b>${esc(i.allegation_ref)}</b></td><td>${esc(i.allegation)}</td>
        <td><b>${esc(i.instrument)}</b><br/>${esc(i.provision)}<br/><i>${esc(i.grant_text)}</i></td>
        <td>${esc(i.effect)}</td><td>${esc(i.strength)}</td></tr>`).join("");
      body = `<table><thead><tr><th>&para;</th><th>Allegation</th><th>Controlling exhibit &amp; grant</th><th>Effect</th><th>Strength</th></tr></thead><tbody>${rows}</tbody></table>`;
    } else if (p.key === "legal_sufficiency") {
      body = (p.items as MtdCount[]).map((i) => `
        <div class="count"><p><b>Count ${esc(i.ordinal)} — ${esc(i.count_name)}</b> &nbsp; <span class="verdict">[${esc(i.verdict).replace(/_/g, " ")}]</span></p>
        ${i.defects?.length ? `<ul>${i.defects.map((d) => `<li>${esc(d)}</li>`).join("")}</ul>` : ""}
        <p>${esc(i.rationale)}</p></div>`).join("");
    } else if (p.key === "facial_defenses") {
      body = (p.items as MtdDefense[]).map((i) => `
        <div class="count"><p><b>${esc(i.defense)}</b> &nbsp; <span class="verdict">[${esc(i.assessment).replace(/_/g, " ")}]</span> — applies to: ${esc(i.applies_to)}</p>
        <p>${esc(i.rationale)}</p></div>`).join("");
    } else if (p.key === "judicial_notice") {
      const rows = (p.items as MtdNotice[]).map((i) => `
        <tr><td><b>${esc(i.allegation_ref)}</b></td><td>${esc(i.allegation)}</td>
        <td><b>${esc(i.record)}</b> (${esc(i.notice_basis).replace(/_/g, " ")})<br/><i>${esc(i.quote)}</i></td>
        <td>${esc(i.effect)}</td><td>${esc(i.strength)}</td></tr>`).join("");
      body = `<table><thead><tr><th>&para;</th><th>Allegation</th><th>Noticeable record &amp; contents</th><th>Effect</th><th>Strength</th></tr></thead><tbody>${rows}</tbody></table>`;
    } else if (p.key === "gaps") {
      const rows = (p.items as GapItem[]).map((i) => `
        <tr><td><b>${esc(i.reference)}</b></td><td>${esc(i.description)}</td><td>${esc(i.cited_at)}</td>
        <td>${i.in_corpus ? "IN CORPUS" + (i.matched_title ? `<br/><i>${esc(i.matched_title)}</i>` : "") : "<b>MISSING</b>"}</td>
        <td>${i.public_record ? "Yes" : "No"}</td><td>${esc(i.source_hint)}</td></tr>`).join("");
      body = `<table><thead><tr><th>Reference</th><th>Description</th><th>Cited at</th><th>Corpus</th><th>Public record</th><th>Source</th></tr></thead><tbody>${rows}</tbody></table>`;
    } else if (p.key === "responses") {
      const rows = (p.items as AnswerResponse[]).map((i) => `
        <tr><td><b>&para;${esc(i.paragraph_number)}</b></td>
        <td><span class="verdict">${esc(i.response).replace(/_/g, " ")}</span>${i.detail ? `<br/>${esc(i.detail)}` : ""}</td>
        <td>${esc(i.rationale)}</td><td><i>${esc(i.evidence_anchor)}</i></td></tr>`).join("");
      body = `<table><thead><tr><th>&para;</th><th>Response</th><th>Rationale</th><th>Evidence anchor</th></tr></thead><tbody>${rows}</tbody></table>`;
    } else if (p.key === "affirmative_defenses") {
      body = (p.items as AnswerDefense[]).map((i) => `
        <div class="count"><p><b>${esc(i.defense)}</b> &nbsp; <span class="verdict">[${esc(i.strength)}]</span> — applies to: ${esc(i.applies_to)}</p>
        ${i.definition ? `<p><i>What it is:</i> ${esc(i.definition)}</p>` : ""}
        ${i.must_prove?.length ? `<p><i>Must prove:</i></p><ul>${i.must_prove.map((m) => `<li>${esc(m)}</li>`).join("")}</ul>` : ""}
        ${i.grounds ? `<p><i>Grounds:</i> ${esc(stripTags(i.grounds))}</p>` : ""}
        <p><i>Basis:</i> ${esc(stripTags(i.basis))}</p>
        ${i.supports?.length ? `<p><i>Supported by:</i> ${i.supports.map((s) => `${esc(s.label)}${s.locator ? ` (${esc(s.locator)})` : ""} — ${esc(s.title)}`).join("; ")}</p>` : ""}
        ${i.evidence?.length ? `<p><i>Evidence:</i></p><ul>${i.evidence.map((e) => `<li>${esc(e.source_label ?? "evidence")}${e.occurred_on ? ` (${esc(e.occurred_on)})` : ""}: “${esc(e.quote)}”</li>`).join("")}</ul>` : ""}</div>`).join("");
    } else if (p.key === "counterclaims") {
      body = (p.items as AnswerCounterclaim[]).map((i) => `
        <div class="count"><p><b>${esc(i.claim_name)}</b> &nbsp; <span class="verdict">[${i.compulsory ? "COMPULSORY — Rule 13(a)" : "permissive"}]</span></p>
        ${i.definition ? `<p><i>What it is:</i> ${esc(stripTags(i.definition))}</p>` : ""}
        ${i.must_prove?.length ? `<p><i>Must prove:</i></p><ul>${i.must_prove.map((m) => `<li>${esc(m)}</li>`).join("")}</ul>` : ""}
        <p>${esc(stripTags(i.theory))}</p>
        ${i.grounds ? `<p><i>Grounds:</i> ${esc(stripTags(i.grounds))}</p>` : ""}
        <p><i>Basis:</i> ${esc(stripTags(i.basis))}</p>
        ${i.supports?.length ? `<p><i>Supported by:</i> ${i.supports.map((s) => `${esc(s.label)}${s.locator ? ` (${esc(s.locator)})` : ""} — ${esc(s.title)}`).join("; ")}</p>` : ""}
        ${i.evidence?.length ? `<p><i>Evidence:</i></p><ul>${i.evidence.map((e) => `<li>${esc(e.source_label ?? "evidence")}${e.occurred_on ? ` (${esc(e.occurred_on)})` : ""}: “${esc(e.quote)}”</li>`).join("")}</ul>` : (i.evidence_basis ? `<p><i>Evidence:</i> ${esc(stripTags(i.evidence_basis))}</p>` : "")}
        ${i.caution ? `<p><i>Caution:</i> ${esc(stripTags(i.caution))}</p>` : ""}</div>`).join("");
    } else if (p.key === "amendment_diff") {
      const rows = (p.items as AmendDiffItem[]).map((i) => `
        <tr><td><span class="verdict">${esc(i.severity)}</span><br/>${esc(i.kind).replace(/_/g, " ")}</td>
        <td><i>${esc(i.original_text)}</i></td>
        <td>${i.amended_text ? esc(i.amended_text) : "<b>— dropped —</b>"}${i.amended_ref ? `<br/>(${esc(i.amended_ref)})` : ""}</td>
        <td>${esc(i.significance)}</td></tr>`).join("");
      body = `<table><thead><tr><th>Change</th><th>Sworn original</th><th>As amended</th><th>Significance</th></tr></thead><tbody>${rows}</tbody></table>`;
    } else if (p.key === "synthesis") {
      body = (p.items as MtdSynth[]).map((i) => `
        <div class="count"><p><b>Count ${esc(i.ordinal)} — ${esc(i.count_name)}</b> &nbsp; <span class="verdict">[${esc(i.predicted_outcome).replace(/_/g, " ")}]</span></p>
        ${i.falling_theories?.length ? `<p><i>Theories that fall:</i></p><ul>${i.falling_theories.map((d) => `<li>${esc(d)}</li>`).join("")}</ul>` : ""}
        <p><i>Survives:</i> ${esc(i.surviving_theory)}</p>
        <p><i>Drivers:</i> ${esc(i.drivers)}</p>
        <p>${esc(i.reasoning)}</p></div>`).join("");
    }
    return `<h2>${esc(p.title)}</h2><p class="intro">${esc(p.intro)}</p>${body}`;
  }).join("\n");

  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${esc(a.title)}</title>
<style>
  body { font-family: "Times New Roman", Georgia, serif; color: #111; margin: 1in; line-height: 1.45; }
  .banner { border: 2px solid #8a1f1f; color: #8a1f1f; padding: 6px 10px; font-weight: bold; text-align: center; margin-bottom: 18px; font-size: 11pt; }
  h1 { font-size: 16pt; text-align: center; margin: 0 0 2px; }
  .meta { text-align: center; font-size: 10pt; color: #444; margin-bottom: 14px; }
  .basis { font-size: 10pt; font-style: italic; color: #333; border-left: 3px solid #999; padding-left: 10px; margin-bottom: 18px; }
  h2 { font-size: 13pt; border-bottom: 1px solid #999; padding-bottom: 3px; margin-top: 22px; }
  .intro { font-size: 11pt; }
  table { border-collapse: collapse; width: 100%; font-size: 10pt; margin-top: 8px; }
  th, td { border: 1px solid #777; padding: 5px 7px; vertical-align: top; text-align: left; }
  th { background: #efefef; }
  .count { margin: 10px 0 14px; font-size: 11pt; }
  .verdict { font-variant: small-caps; color: #8a1f1f; }
  .caveats { margin-top: 26px; font-size: 9.5pt; color: #555; border-top: 1px solid #999; padding-top: 8px; }
  @media print { body { margin: 0.7in; } }
</style></head><body>
<div class="banner">DRAFT — ATTORNEY WORK PRODUCT — FOR COUNSEL REVIEW ONLY</div>
<h1>${esc(a.title)}</h1>
<div class="meta">Generated ${esc(dt)} · ${esc(c.build ?? "")}</div>
<div class="basis">${esc(c.basis ?? "")}</div>
${sections}
<div class="caveats">${esc(c.caveats ?? "")}</div>
</body></html>`;
}

export function downloadWord(a: StrikeArtifact): void {
  const html = memoHtml(a);
  const blob = new Blob(["﻿", html], { type: "application/msword" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${a.title.replace(/[^a-z0-9]+/gi, "-")}.doc`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function printPdf(a: StrikeArtifact): void {
  const w = window.open("", "_blank");
  if (!w) return;
  w.document.write(memoHtml(a));
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 350);
}
